/**
 * A ratio as a percentage, to the number of decimals a page prints it to.
 *
 * `format.fraction` is the rendering — one `Intl` call, one rounding, the minus sign
 * — and this is the vocabulary over it: **how many decimals**, and which of the two
 * shapes a caller wants. Both are decisions the mockups make and neither is a
 * formatter's.
 *
 * It is one file because it was two. `pages/studio/analytics/render.ts` exported
 * `share`/`ratio` with `SHARE_DIGITS = 1` and `RATIO_DIGITS = 0`;
 * `pages/studio/clients/percent.ts` exported `percentText`/`percentFigure` with
 * `SHARE_DIGITS = 1` and `WHOLE_DIGITS = 0`. `share(x, format)` and
 * `percentText(format, x, 1)` were the same call with the arguments in the other
 * order, and the same constant meant the same thing under two names — which is the
 * shape of duplication that reads as a distinction and is not one.
 *
 * **The sign is separate for the hero.** `clients.html` prints `71.0<sup>%</sup>`, so
 * `HeroFigure`'s `sup` prop wants the figure without it. `percentFigure` is that, and
 * it is the same rounding as `percentText` with one `Intl` part dropped rather than a
 * second calculation — see `formatFraction`.
 */

import type { Formatter } from './useFormat.js'

/**
 * A share of a whole prints to one decimal.
 *
 * `analytics.html`: `65.0%`, `1.5%`, `80.1%`. `clients.html`'s concentration figures:
 * `71.0`, `38.4`, `19.1`. Two pages, one rule.
 */
export const SHARE_DIGITS = 1

/**
 * A ratio that is a multiple rather than a share prints whole.
 *
 * An allowance burn (`114%`), value against paid (`183%`), a rate against the floor.
 * A tenth of a percent on a figure that runs past 100 is precision nobody reads.
 */
export const WHOLE_DIGITS = 0

/** `0.801025641` → `80.1%`. */
export function percentText(format: Formatter, ratio: number, fractionDigits: number): string {
  return format.fraction(ratio, { fractionDigits })
}

/** `0.7097…` → `71.0`. The figure alone; the caller adds the sign as an element. */
export function percentFigure(format: Formatter, ratio: number, fractionDigits: number): string {
  return format.fraction(ratio, { fractionDigits, sign: false })
}

/** `0.801025641` → `80.1%` — a share, at the shared number of decimals. */
export function share(format: Formatter, ratio: number): string {
  return percentText(format, ratio, SHARE_DIGITS)
}

/** `1.8269` → `183%` — a multiple, whole. */
export function whole(format: Formatter, ratio: number): string {
  return percentText(format, ratio, WHOLE_DIGITS)
}
